import React, { useState, useEffect } from 'react';
import { View } from 'react-native';
import { makeStyles } from '../hooks/MakeStylesHook';
import { TextInput } from './index';
import CountryPicker from './countryPicker';
import {
  getCountryCallingCode,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  validatePhoneNumberLength,
  parsePhoneNumber,
  AsYouType,
} from 'libphonenumber-js/max';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

const InternationalPhonePicker = ({
  value,
  onChange,
  onCountryChange,
  countryCode = 'IN',
  label = 'Mobile Number',
  error,
  errorText,
  placeholder,
  required = false,
  onSubmit,
  customErrorMessages,
  customStyles = {},
  placeholderTextSize,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const styles = useStyles(theme);
  const [phoneNumber, setPhoneNumber] = useState(value || '');
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const stored = sessionStorage.getItem('countryCode');
    return stored || countryCode || 'IN';
  });
  const [phoneError, setPhoneError] = useState(error || false);
  const [phoneErrorText, setPhoneErrorText] = useState(errorText || '');
  const [touched, setTouched] = useState(false);

  const defaultErrorMessages = {
    required: t('error_mobile_no_blank') || 'Mobile number cannot be empty',
    invalid: t('error_mobile_no_not_valid') || 'Mobile number not valid',
    tooShort: t('error_mobile_at_least') || 'Mobile Number is too short',
    notMobile: t('error_not_mobile_number') || 'Not a valid mobile number',
  };

  const errorMessages = { ...defaultErrorMessages, ...customErrorMessages };

  useEffect(() => {
    if (error !== undefined) {
      setPhoneError(error);
    }
    if (errorText) {
      setPhoneErrorText(errorText);
    }
  }, [error, errorText]);

  useEffect(() => {
    if (value !== undefined && value !== phoneNumber) {
      setPhoneNumber(value || '');
      if (!touched) {
        setPhoneError(false);
        setPhoneErrorText('');
      }
    }
  }, [value]);

  useEffect(() => {
    const storedCountryCode = sessionStorage.getItem('countryCode');
    if (storedCountryCode && storedCountryCode !== selectedCountry) {
      setSelectedCountry(storedCountryCode);
    } else if (
      countryCode &&
      countryCode !== selectedCountry &&
      !storedCountryCode
    ) {
      setSelectedCountry(countryCode);
    }
  }, [countryCode]);

  const formatPhoneNumber = (text, country) => {
    const formatter = new AsYouType(country);
    return formatter.input(text);
  };

  const cleanPhoneNumber = (text) => {
    return text.replace(/\D/g, '');
  };

  const validatePhoneNumber = (text, country) => {
    if (!text || text.trim() === '') {
      return {
        isValid: !required,
        error: required ? errorMessages.required : '',
      };
    }

    try {
      const isPossible = isPossiblePhoneNumber(text, country);
      if (!isPossible) {
        return {
          isValid: false,
          error: errorMessages.invalid,
        };
      }

      const isValid = isValidPhoneNumber(text, country);
      if (!isValid) {
        return {
          isValid: false,
          error: errorMessages.invalid,
        };
      }

      const lengthValidation = validatePhoneNumberLength(text, country);
      if (lengthValidation === 'TOO_SHORT') {
        return {
          isValid: false,
          error: errorMessages.tooShort,
        };
      }

      return {
        isValid: true,
        error: '',
      };
    } catch (error) {
      return {
        isValid: false,
        error: errorMessages.invalid,
      };
    }
  };

  const getFormattedNumber = (text, country) => {
    if (!text) return '';

    const cleanNumber = cleanPhoneNumber(text);
    return `+${getCountryCallingCode(country)}${cleanNumber}`;
  };

  const handlePhoneChange = (text) => {
    const formattedText = formatPhoneNumber(text, selectedCountry);
    setPhoneNumber(formattedText);
    const validation = validatePhoneNumber(formattedText, selectedCountry);
    setPhoneError(false);
    setPhoneErrorText('');
    const formattedNumber = getFormattedNumber(formattedText, selectedCountry);

    if (onChange) {
      onChange({
        phoneNumber: formattedText,
        formattedNumber,
        countryCode: selectedCountry,
        isValid: validation.isValid,
        validationError: validation.error,
      });
    }
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    sessionStorage.setItem('countryCode', country);

    if (phoneNumber) {
      const cleanedNumber = cleanPhoneNumber(phoneNumber);
      setPhoneNumber(cleanedNumber);
      const validation = validatePhoneNumber(cleanedNumber, country);

      if (touched && cleanedNumber.trim() !== '') {
        setPhoneError(!validation.isValid);
        setPhoneErrorText(validation.error);
      } else {
        setPhoneError(false);
        setPhoneErrorText('');
      }

      const formattedNumber = getFormattedNumber(cleanedNumber, country);
      if (onChange) {
        onChange({
          phoneNumber: cleanedNumber,
          formattedNumber,
          countryCode: country,
          isValid: validation.isValid,
          validationError: validation.error,
        });
      }
    }

    if (onCountryChange) {
      onCountryChange(country);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (phoneNumber && phoneNumber.trim() !== '') {
      const validation = validatePhoneNumber(phoneNumber, selectedCountry);
      setPhoneError(!validation.isValid);
      setPhoneErrorText(validation.error);
    } else if (required) {
      setPhoneError(true);
      setPhoneErrorText(errorMessages.required);
    }

    if (onChange) {
      const validation = validatePhoneNumber(phoneNumber, selectedCountry);
      onChange({
        phoneNumber: phoneNumber,
        formattedNumber: getFormattedNumber(phoneNumber, selectedCountry),
        countryCode: selectedCountry,
        isValid: validation.isValid,
        validationError: validation.error,
      });
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.countryPickerWrapper}>
        <CountryPicker
          selectedCountry={selectedCountry}
          onSelectCountry={handleCountryChange}
          label={`Country/Region${required ? '*' : ''}`}
          error={phoneError}
          customStyles={{
            ...styles.defaultCountryPicker,
            ...customStyles.countryPicker,
          }}
        />
      </View>
      <TextInput
        keyboardType={'numeric'}
        label={`${label}${required ? '*' : ''}`}
        returnKeyType="next"
        value={phoneNumber}
        onChangeText={handlePhoneChange}
        onBlur={handleBlur}
        error={phoneError}
        errorText={phoneErrorText}
        placeholder={placeholder || ''}
        onSubmitEditing={onSubmit}
        maxLength={20}
        style={[
          styles.defaultTextInput,
          customStyles.textInput,
          customStyles.labelStyle && {
            fontSize: customStyles.labelStyle.fontSize,
          },
        ]}
        placeholderTextSize={placeholderTextSize}
        contentStyle={customStyles.labelStyle}
      />
    </View>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    position: 'relative',
    zIndex: 1,
  },
  countryPickerWrapper: {
    position: 'relative',
    zIndex: 10000,
    marginBottom: 5,
  },
  searchInputStyle: {
    outline: 'none',
    borderColor: 'transparent',
  },
  defaultTextInput: {
    fontSize: 13,
    backgroundColor: theme.colors.onBackground,
    position: 'relative',
    zIndex: 1,
  },
  defaultCountryPicker: {
    backgroundColor: theme.colors.onBackground,
    borderColor: theme.colors.borderColor,
  },
}));

export default InternationalPhonePicker;
